import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '../../api/client';
import { Button, Modal } from '../common';
import { TODO_STATUS } from '../../constants';

/**
 * CreateTodoModal - Create or edit todos during topic discussion
 */
const CreateTodoModal = ({ topicId, todo, users, onClose, onSave }) => {
  const isEditMode = Boolean(todo);

  const [title, setTitle] = useState(todo?.title || '');
  const [details, setDetails] = useState(todo?.details || '');
  const [dueDate, setDueDate] = useState(
    todo?.dueDate ? todo.dueDate.split('T')[0] : ''
  );
  const [status, setStatus] = useState(todo?.status || TODO_STATUS.NOT_STARTED);
  const [assignee, setAssignee] = useState(() => {
    if (!todo?.assignees?.length) return '';
    const first = todo.assignees[0];
    return typeof first === 'object' ? String(first.id) : String(first);
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [sortBy, setSortBy] = useState('firstName');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!dueDate) {
      setError('Due Date is required');
      return;
    }
    if (!assignee) {
      setError('Please assign this task to someone');
      return;
    }

    setSaving(true);
    try {
      if (isEditMode) {
        await api.put(`/todos/${todo.id}`, {
          title: title.trim(),
          details,
          dueDate,
          status,
          assignees: [Number(assignee)],
        });
      } else {
        await api.post('/todos', {
          topicId,
          title: title.trim(),
          details,
          dueDate,
          status,
          assignees: [assignee],
        });
      }
      onSave();
    } catch (err) {
      setError(err.message || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      if (sortBy === 'firstName') {
        return a.firstName.localeCompare(b.firstName);
      } else {
        return a.lastName.localeCompare(b.lastName);
      }
    });
  }, [users, sortBy]);

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">{isEditMode ? 'Edit Task' : 'Add Task'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group">
              <label className="form-label">
                Title <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input
                type="text"
                className="form-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Details</label>
              <textarea
                className="form-input form-textarea"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label className="form-label">
                Due Date <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input
                type="date"
                className="form-input"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-input"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value={TODO_STATUS.NOT_STARTED}>{TODO_STATUS.NOT_STARTED}</option>
                <option value={TODO_STATUS.IN_PROGRESS}>{TODO_STATUS.IN_PROGRESS}</option>
                <option value={TODO_STATUS.DONE}>{TODO_STATUS.DONE}</option>
              </select>
            </div>
            <div className="form-group">
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <label className="form-label" style={{ marginBottom: 0 }}>
                  Assign To <span style={{ color: 'var(--color-danger)' }}>*</span>
                </label>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant={sortBy === 'firstName' ? 'primary' : 'secondary'}
                    size="small"
                    onClick={() => setSortBy('firstName')}
                  >
                    First Name
                  </Button>
                  <Button
                    type="button"
                    variant={sortBy === 'lastName' ? 'primary' : 'secondary'}
                    size="small"
                    onClick={() => setSortBy('lastName')}
                  >
                    Last Name
                  </Button>
                </div>
              </div>
              <select
                className="form-input"
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
              >
                <option value="">Select a person...</option>
                {sortedUsers.map(user => (
                  <option key={user.id} value={String(user.id)}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Save')}
            </Button>
          </Modal.Footer>
        </form>
      </div>
    </div>
  );
};

CreateTodoModal.propTypes = {
  topicId: PropTypes.number.isRequired,
  todo: PropTypes.object,
  users: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default CreateTodoModal;
